import Card from "@/UI/Card";
import Table from "@/UI/Table";
import {
  clearEditNote,
  editNote,
  loadAllNotePaginated,
} from "@/redux/rtk/features/CRM/note/noteSlice";
import { EyeOutlined, PlusOutlined } from "@ant-design/icons";
import { Drawer, Modal } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import AddNote from "../CRM/Note/AddNote";
import DetailsNote from "../CRM/Note/DetailsNote";
import UserPrivateComponent from "../PrivacyComponent/UserPrivateComponent";

export default function Notes({ data, loading: parentLoading, name, singleLoadThunk }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { list: noteList, loading: noteLoading, edit } = useSelector(
    (state) => state.note
  );
  const [open, setOpen] = useState(false);

  const onClose = () => {
    setOpen(false);
  };

  const fetchNotes = () => {
    if (name && data?.id) {
      dispatch(loadAllNotePaginated({ [name]: data.id }));
    }
  };

  useEffect(() => {
    fetchNotes();
  }, [name, data?.id, dispatch]);

  const columns = [
    {
      title: t("common.title"),
      key: "title",
      dataIndex: "title",
      render: (title, note) => (
        <span
          className="cursor-pointer font-medium text-primary hover:underline"
          onClick={() => dispatch(editNote(note))}>
          {title}
        </span>
      ),
    },
    {
      title: t("common.owner"),
      dataIndex: "noteOwner",
      key: "owner",
      render: (noteOwner, item) => (
        <Link to={`/admin/staff/${item?.noteOwnerId}`}>
          {noteOwner?.firstName} {noteOwner?.lastName}
        </Link>
      ),
    },
    {
      title: t("common.description"),
      key: "description",
      dataIndex: "description",
      render: (description, item) =>
        description ? (
          <span
            className="cursor-pointer hover:underline text-gray-600 dark:text-gray-300"
            onClick={() => dispatch(editNote(item))}>
            {description.length > 80 ? `${description.slice(0, 80)}...` : description}
          </span>
        ) : (
          "-"
        ),
    },
    {
      title: t("common.createDate"),
      key: "Create date",
      dataIndex: "createdAt",
      render: (date) => moment(date).format("MMMM Do YYYY"),
    },
    {
      id: 5,
      title: "",
      key: "action",
      render: (item) => [
        {
          label: (
            <span
              className="cursor-pointer"
              onClick={() => dispatch(editNote(item))}>
              <button className="flex justify-center items-center gap-2 rounded text-primary border border-primary/20 px-3 py-1 bg-primary/5 hover:bg-primary/10 transition-colors">
                <EyeOutlined /> {t("common.view")}
              </button>
            </span>
          ),
          key: "view",
        },
      ],
      csvOff: true,
    },
  ];

  return (
    <Card
      className="border-none shadow-none"
      title={
        <div className="flex items-center gap-2">
          <span className="font-bold text-lg">{t("common.notes")}</span>
          <UserPrivateComponent permission="create-note">
            <PlusOutlined
              onClick={() => setOpen(true)}
              className="bg-primary text-white cursor-pointer rounded-full text-[12px] p-2 hover:opacity-90 transition-opacity"
            />
          </UserPrivateComponent>
        </div>
      }>
      <div className="mt-2">
        <UserPrivateComponent permission="readAll-note">
          <Table
            columns={columns}
            loading={noteLoading || parentLoading}
            data={noteList || []}
          />
        </UserPrivateComponent>

        <UserPrivateComponent permission="create-note">
          <div className="mt-6 flex justify-center">
            <button
              onClick={() => setOpen(true)}
              className="flex items-center justify-center gap-2 w-full py-3 px-4 rounded-xl border-2 border-dashed border-primary/30 text-primary hover:border-primary hover:bg-primary/5 transition-all font-medium"
            >
              <PlusOutlined /> {t("common.createNote")}
            </button>
          </div>
        </UserPrivateComponent>
      </div>

      <UserPrivateComponent permission="create-note">
        <Drawer
          onClose={onClose}
          open={open}
          title={t("common.createNote")}
          width={window.innerWidth <= 768 ? "100%" : "45%"}
          placement="right">
          <div className="mt-4">
            <AddNote
              createAs={{
                values: { [name]: data?.id },
                singleLoadThunk: () => {
                  fetchNotes();
                  if (singleLoadThunk) dispatch(singleLoadThunk(data.id));
                },
                id: data?.id,
                name,
              }}
              drawer={true}
            />
          </div>
        </Drawer>
      </UserPrivateComponent>

      <UserPrivateComponent permission={"readSingle-note"}>
        <Modal
          footer={null}
          open={!!edit}
          onCancel={() => dispatch(clearEditNote())}
          width={window.innerWidth > 768 ? "60%" : "100%"}
          closeIcon={null}>
          <DetailsNote
            note={edit}
            onClose={() => dispatch(clearEditNote())}
            load={() => {
              fetchNotes();
              if (singleLoadThunk) dispatch(singleLoadThunk(data.id));
            }}
          />
        </Modal>
      </UserPrivateComponent>
    </Card>
  );
}
